import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const NumField = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    <Input
      data-testid={testId}
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className="mt-1.5 bg-[#0F172A] num"
    />
  </label>
);

const ListField = ({ label, hint, value, onChange, testId }) => {
  const [text, setText] = useState(value.join(", "));
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <Input
        data-testid={testId}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange(text.split(",").map((s) => s.trim()).filter(Boolean))}
        placeholder="Any (leave blank)"
        className="mt-1.5 bg-[#0F172A]"
      />
      <span className="text-xs text-slate-500 mt-1 block">{hint}</span>
    </label>
  );
};

const Toggle = ({ label, hint, checked, onChange, testId, disabled }) => (
  <label className={`flex items-start gap-3 bg-[#0F172A] border border-slate-800 rounded p-4 ${disabled ? "opacity-80" : "cursor-pointer"}`}>
    <Switch checked={!!checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} className="mt-0.5" />
    <span>
      <span className="block text-sm text-white">{label}</span>
      <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>
    </span>
  </label>
);

export const RulesEditor = ({ rules, onChange }) => {
  const set = (k) => (v) => onChange({ ...rules, [k]: v });
  return (
    <div className="space-y-8" data-testid="rules-editor">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumField label="Field Size Min" value={rules.field_size_min} onChange={set("field_size_min")} testId="rule-field-min-input" />
        <NumField label="Field Size Max" value={rules.field_size_max} onChange={set("field_size_max")} testId="rule-field-max-input" />
        <NumField label="Track Rating Min" value={rules.track_rating_min} onChange={set("track_rating_min")} testId="rule-track-min-input" />
        <NumField label="Track Rating Max" value={rules.track_rating_max} onChange={set("track_rating_max")} testId="rule-track-max-input" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Toggle label="Australian races only" hint="Thoroughbred races in Australia (NSW, VIC, QLD, SA, WA, TAS, ACT, NT)." checked={rules.australia_only} onChange={set("australia_only")} testId="rule-australia-only-switch" />
        <Toggle label="No first starters (always on)" hint="Any race with a first starter is never rated." checked onChange={() => {}} disabled testId="rule-no-first-starters-switch" />
        <Toggle label="No resumers" hint="Race fails if any runner's form ends in x/X (first-up from a spell). A '0' last start is fine." checked={rules.no_resumers} onChange={set("no_resumers")} testId="rule-no-resumers-switch" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <NumField label="Distance Min (m)" value={rules.distance_min} onChange={set("distance_min")} testId="rule-distance-min-input" />
        <NumField label="Distance Max (m)" value={rules.distance_max} onChange={set("distance_max")} testId="rule-distance-max-input" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <ListField label="Allowed Classes" hint="Comma separated, e.g. Benchmark, Class 3, Group. Blank = any class." value={rules.allowed_classes} onChange={set("allowed_classes")} testId="rule-classes-input" />
        <ListField label="Excluded Keywords" hint="Race fails if class or name contains any, e.g. Maiden, 2YO." value={rules.excluded_keywords} onChange={set("excluded_keywords")} testId="rule-excluded-input" />
        <ListField label="Track Conditions" hint="e.g. Good, Soft. Matches 'Good 4', 'Soft 5'. Blank = any." value={rules.track_conditions} onChange={set("track_conditions")} testId="rule-track-input" />
        <ListField label="Venues" hint="e.g. Randwick, Flemington. Blank = any venue." value={rules.venues} onChange={set("venues")} testId="rule-venues-input" />
      </div>
      <p className="text-xs text-slate-500">Track rating uses the number in the track condition (Good 4 = 4, Soft 6 = 6, Heavy 8 = 8). Scratched horses and emergencies are not counted. Leave a min/max blank to switch that rule off. A race qualifies only when every active rule passes.</p>
    </div>
  );
};
